import { Component } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import type { ToasterProps } from '@spartan-ng/brain/sonner';
import { UserService } from './service/user/user.service';
import { AuthService } from './service/auth/auth.service';
import { environment } from '../environments/environment';
import { HlmToaster } from '@spartan-ng/helm/sonner';
import { NotificationService } from './service/core/notification.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    RouterOutlet,
    HlmToaster,
  ]
})
export class AppComponent {
  title = 'app';

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private notificationService: NotificationService,
  ) {
    this.authService.initializeSessionHandling();
    this.verifyUserRoles();
  }

  /**
   * On a phone a notification takes the whole width of the screen. Sonner hangs the close button on the top-left corner of the
   * toast, half outside of it: on a toast that touches the edge of the screen a 20px button is cut by it. There it goes inside
   * the corner, 32px wide, and the text keeps clear of it.
   */
  readonly toastOptions: ToasterProps['toastOptions'] = {
    classes: {
      toast: 'max-[600px]:w-full! max-[600px]:pe-12!',
      closeButton: 'max-[600px]:size-8! max-[600px]:top-2! max-[600px]:right-2! max-[600px]:left-auto! max-[600px]:[transform:none]!'
    }
  };

  /**
   * Load the current user profile whenever a session exists (also after a silent SSO or a reload,
   * when nothing is cached yet). If a profile was already cached, verify that the user roles from the
   * token are the same as the ones from the backend; if they are different, the user is logged out.
   */
  verifyUserRoles() {
    const hadCachedUser = !!this.authService.getUserFromLocalStorage();

    this.authService.isLoggedIn().subscribe(isLogged => {
      if (!isLogged) {
        return;
      }

      this.userService.getCurrentUser().subscribe({
        next: user => {
          this.authService.saveUserToLocalStorage(user);

          if (!hadCachedUser) {
            return;
          }

          const userRolesFromToken = this.authService.getUserRolesFromToken();
          const userRoles = user.roles;

          if (!this.areEqualsRoles(userRoles, userRolesFromToken)) {
            this.notificationService.showWarning(
              'Sessione non valida',
              'Sei stato disconnesso perché i tuoi ruoli sono cambiati. Effettua nuovamente il login.'
            );
            this.authService.logout(`${environment.MANAGER_FRONTEND_URL}/login`);
          }
        },
        error: () => { /* silently ignore — handled by ErrorsInterceptor */ }
      });
    });
  }

  areEqualsRoles(rolesFromBackend: string[], rolesFromToken: string[]): boolean {
    const setA = new Set(rolesFromBackend);
    const setB = new Set(rolesFromToken);
    if (setA.size !== setB.size) {
      return false;
    }
    for (const item of setA) {
      if (!setB.has(item)) {
        return false;
      }
    }
    return true;
  }
}
